/******/ (() => { // webpackBootstrap
if (!customElements.get('theme-modal')) {
  const Z_INDEX = {
    MODAL_ABOVE_DRAWER: '1400',
    MODAL_DEFAULT: '1901'
  };
  
  class ThemeModal extends HTMLElement {
    constructor() {
      super();
      this.modalId = 'ThemeModal';
      this.modalContentSelector = '[data-modal-content] .theme-modal--inner';
      this.focusableSelector = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
      this.restoreFocusOnClose = this.getAttribute('data-focus-on-close');
      this.cacheDOMElements();
      this.bindEventHandlers();
      this.handleProductModalReady = this.handleProductModalReady.bind(this);
      window._myModalCache = window._myModalCache || {};
      window.__themeModalInflight = window.__themeModalInflight || {};

      // Check if a drawer is opened
      this.bodyClassObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (document.body.className.includes('js-drawer-open')) {
              if (this.overlay) this.overlay.removeEventListener('click', this.hideModal);
            } else {
              if (this.overlay) this.overlay.addEventListener('click', this.hideModal);
            }
          }
        }
      });
    }

    cacheDOMElements() {
      this.sectionId = this.getAttribute('data-section-id');
      this.sectionFetchId = this.getAttribute('data-section-fetch-id');
      this.productUrl = this.getAttribute('data-product-url');
      this.contentSelector = this.getAttribute('data-content-selector');
      this.shouldFetchSection = this.hasAttribute('data-fetch-section');
      this.blockId = this.getAttribute('data-block-id');
      this.modalButton = this.querySelector('[data-modal-button]');
      this.modalTemplate = this.querySelector('template[data-theme-modal-content]');
      this.overlay = document.querySelector('#DrawerOverlay');
      this.modalContent = this.querySelector('[data-modal-content]');
      this.isQuickView = this.hasAttribute('data-is-quick-view');

      // Early return if required elements are missing
      if (!this.modalButton || !this.modalTemplate) {
        console.warn('Theme Modal: Required elements missing');
        return;
      }
    }

    bindEventHandlers() {
      this.showModal = this.showModal.bind(this);
      this.hideModal = this.hideModal.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDrawerOpened = this.handleDrawerOpened.bind(this);
      this.handleDrawerClosed = this.handleDrawerClosed.bind(this);
      this.prefetch = this.prefetch.bind(this);
    }

    getModalFetchUrl() {
      return this.productUrl || (this.sectionFetchId ? `${window.Shopify.routes.root}?section_id=${this.sectionFetchId}` : '');
    }

    /**
     * One network request per URL: click + hover prefetch share the same promise and cache.
     */
    async ensureModalHtmlLoaded() {
      const url = this.getModalFetchUrl();
      if (!url) return '';

      if (Object.prototype.hasOwnProperty.call(window._myModalCache, url)) {
        const cached = window._myModalCache[url];
        this._prefetchedModalHTML = cached;
        return cached;
      }

      let inflight = window.__themeModalInflight[url];
      if (!inflight) {
        inflight = (async () => {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch modal content: ${response.status}`);
          }
          const textContent = await response.text();
          const doc = new DOMParser().parseFromString(textContent, 'text/html');
          const relevantContent = this.extractModalMarkupFromDoc(doc, textContent) || '';
          window._myModalCache[url] = relevantContent;
          return relevantContent;
        })();
        inflight.finally(() => {
          if (window.__themeModalInflight[url] === inflight) {
            delete window.__themeModalInflight[url];
          }
        });
        window.__themeModalInflight[url] = inflight;
      }

      const html = await inflight;
      this._prefetchedModalHTML = html;
      return html;
    }

    /**
     * Quick view markup lives in <template data-quick-view-product>; DOMParser often needs
     * template.content, not template.innerHTML. Other modals use data-content-selector only.
     */
    extractModalMarkupFromDoc(doc, fullPageHtml) {
      const templateEl = doc.querySelector('template[data-quick-view-product]');
      if (templateEl?.content?.childNodes?.length) {
        const wrap = doc.createElement('div');
        wrap.appendChild(templateEl.content.cloneNode(true));
        const html = wrap.innerHTML;
        if (html && html.trim()) return html;
      }

      if (this.contentSelector) {
        const match = doc.querySelector(this.contentSelector);
        if (match?.innerHTML) return match.innerHTML;
      }

      return this.contentSelector ? '' : fullPageHtml;
    }

    connectedCallback() {
      // Listen for generic modal hide event
      window.eventBus.on('theme:modal:hide', this.hideModal);

      window.eventBus.on('product:modal:ready', this.handleProductModalReady);

      // Show modal when button is clicked
      this.modalButton.addEventListener('click', this.showModal);
      /* Start fetch before click completes (saves ~100–400ms vs waiting for the click handler alone). */
      this.modalButton.addEventListener('pointerdown', this.prefetch, { passive: true });
      this.modalButton.addEventListener('focusin', this.prefetch);

      // Change modal z-index when drawer opens
      window.eventBus.on('drawer:opened', this.handleDrawerOpened);

      // Change modal z-index when drawer closes
      window.eventBus.on('drawer:closed', this.handleDrawerClosed);

      // Prefetch modal content when product card is hovered
      if (this.shouldFetchSection) {
        const productCard = this.closest('product-card') || this.closest('[data-quick-view-fetch-target]');
        if (productCard) {
          productCard.addEventListener('mouseenter', this.prefetch);
          productCard.addEventListener('touchstart', this.prefetch, { passive: true });
        }
      }

      // Handle editor events
      if (window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', async () => {
          // Hide the modal if it's already open
          this.hideModal();
        });
      }
    }

    handleDrawerOpened() {
      const modal = document.querySelector('#ThemeModal');
      if (modal) {
        modal.style.zIndex = Z_INDEX.MODAL_ABOVE_DRAWER;
      }
    }
      
    handleDrawerClosed() {
      const modal = document.querySelector('#ThemeModal');
      if (modal) {
        setTimeout(() => {
          modal.style.zIndex = Z_INDEX.MODAL_DEFAULT;
        }, 600);
      }
    }

    disconnectedCallback() {
      // Stop observing the body class
      this.bodyClassObserver.disconnect();

      // Clean up event listeners
      this.modalButton.removeEventListener('click', this.showModal);
      this.modalButton.removeEventListener('pointerdown', this.prefetch);
      this.modalButton.removeEventListener('focusin', this.prefetch);
      if (this.overlay) this.overlay.removeEventListener('click', this.hideModal);
      if (this.dismissButton) this.dismissButton.removeEventListener('click', this.hideModal);

      // Remove event listeners for drawer events
      window.eventBus.off('drawer:opened', this.handleDrawerOpened);
      window.eventBus.off('drawer:closed', this.handleDrawerClosed);
      window.eventBus.off('product:modal:ready', this.handleProductModalReady);

      window.eventBus.off('theme:modal:hide', this.hideModal);
    }

    prefetch() {
      const url = this.getModalFetchUrl();
      if (!url) return;
      if (Object.prototype.hasOwnProperty.call(window._myModalCache, url)) {
        this._prefetchedModalHTML = window._myModalCache[url];
        return;
      }
      this.ensureModalHtmlLoaded().catch((err) => {
        console.error('Prefetch error:', err);
      });
    }

    /**
     * Bold Subscriptions (bsub.js) only runs on DOMContentLoaded. Quick view injects
     * [data-bsub-widget] later, so we re-parse product JSON, refresh prices, and
     * re-dispatch the selected purchase option so the delivery-frequency plans show.
     */
    handleProductModalReady() {
      const tryReinit = (attempt) => {
        const bold = window.BOLD && window.BOLD.BsubWidget;
        if (!bold || typeof bold.reinit !== 'function') {
          if (attempt < 50) {
            setTimeout(() => tryReinit(attempt + 1), 50);
          }
          return;
        }
        const modal = document.querySelector('#ThemeModal');
        if (!modal || !modal.classList.contains('is-quick-view')) {
          return;
        }
        const bsub = modal.querySelector('[data-bsub-widget]');
        if (!bsub) {
          return;
        }
        const root =
          bsub.closest('[data-modal-content]') || bsub.closest('.theme-modal--inner') || modal;
        bold.reinit(root);
      };
      tryReinit(0);
    }

    initScrollHandler(el) {
      if (!el) return;
      let scrolling;

      el.addEventListener('scroll', () => {
        clearTimeout(scrolling);
        el.classList.add('scrolling');
        scrolling = setTimeout(() => {
          el.classList.remove('scrolling');
        }, 800);
      });
    }

    async render() {
      try {
        const modalContent = document.querySelector(`#${this.modalId}`);
        if (!modalContent) {
          console.warn('Theme Modal: Modal content element not found');
          return false;
        }

        const fetchUrl = this.getModalFetchUrl();
        if (fetchUrl && Object.prototype.hasOwnProperty.call(window._myModalCache, fetchUrl)) {
          this._prefetchedModalHTML = window._myModalCache[fetchUrl];
        }
    
        // Insert your template
        modalContent.innerHTML = this.modalTemplate.innerHTML;
        this.modalContent = modalContent;
        this.dismissButton = this.modalContent.querySelector('[data-close]');
    
        // Set up the dismiss button, etc.
        if (this.dismissButton) {
          this.dismissButton.addEventListener('click', this.hideModal);
        }
    
        // If we have prefetched content in memory, just inject it, no fetch
        if (this._prefetchedModalHTML) {
          const targetElement = document.querySelector(`#${this.modalId} ${this.modalContentSelector}`);
          if (!targetElement) {
            console.error('Modal content target element not found');
            return false;
          }
          targetElement.innerHTML = this._prefetchedModalHTML;
          // Emit an event that the content is ready
          this.isQuickView ?
            window.eventBus.emit('product:modal:ready', { detail: { sectionId: this.quickViewSectionId } })
            : window.eventBus.emit('product:modal:ready', { detail: { sectionId: this.sectionId } });
        }
        // otherwise do the original fetch if needed
        else if (this.shouldFetchSection && (this.sectionFetchId || this.productUrl)) {
          await this.fetchModalContent(); // your original fetch that also injects into DOM
        }
    
        return true;
      } catch (error) {
        console.error('Theme Modal: Error rendering modal', error);
        return false;
      }
    }    

    async showModal(e) {
      e.stopPropagation();
      e.preventDefault();

      const themeModalShell = document.querySelector('#ThemeModal');

      /**
       * #ThemeModal defaults to centered translate(-50%,-50%). Adding is-quick-view switches to
       * translate3d(105%,0,0). Without killing transitions first, the browser animates that
       * change (looks like a diagonal / “top to right” flash); second open often feels fine due to timing.
       */
      const flushQuickViewDrawerMotion = () => {
        if (!themeModalShell || !this.isQuickView) return;
        /* custom.css uses transition/animation with !important; plain style.transition loses the cascade. */
        themeModalShell.style.setProperty('transition', 'none', 'important');
        themeModalShell.style.setProperty('animation', 'none', 'important');
        void themeModalShell.offsetHeight;
        themeModalShell.style.removeProperty('transition');
        themeModalShell.style.removeProperty('animation');
      };

      if (this.isQuickView && themeModalShell) {
        themeModalShell.classList.add('is-quick-view');
        flushQuickViewDrawerMotion();
      }

      // Emit an event that the modal is being shown
      window.eventBus.emit('theme:modal:open', { details: { sectionId: this.sectionId } });

      // Start observing the body class
      this.bodyClassObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    
      // Store the element that was focused before opening the modal
      this.previousActiveElement = e.currentTarget;
    
      const rendered = await this.render();
      if (rendered) {
        const afterOpen = () => {
          document.body.classList.add('theme-modal-open');
          if (this.modalContent) {
            this.quickViewProduct = this.modalContent.querySelector('[data-quick-view-product] [data-product-content-wrapper]');
            this.quickViewSectionId = this.quickViewProduct?.getAttribute('data-section-id');
            this.modalContent.classList.remove('hidden');

            this.trapFocus();

            const scrollableInner = this.modalContent.querySelector('[data-modal-content]');
            if (scrollableInner) {
              this.initScrollHandler(scrollableInner);
            }
          }
        };

        /* Quick view: extra frame so injected layout settles before the slide (reduces first-open jitter). */
        if (this.isQuickView) {
          flushQuickViewDrawerMotion();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(afterOpen);
            });
          });
        } else {
          afterOpen();
        }
      }
    }    

    hideModal() {
      const modalIsOpen = document.body.classList.contains('theme-modal-open');
      // Remove the open class to trigger the CSS fade-out/transform transition
      document.body.classList.remove('theme-modal-open');

      // Emit an event that the modal is being closed
      window.eventBus.emit('theme:modal:close', { details: { sectionId: this.sectionId } });
    
      // Emit event before cleaning up (if other parts of your code need to know)
      this.isQuickView ?
        window.eventBus.emit('product:modal:close', { detail: { sectionId: this.quickViewSectionId, isQuickView: true } })
        : window.eventBus.emit('product:modal:close', { detail: { sectionId: this.sectionId, isQuickView: false } });
    
      // Remove keydown listener
      document.removeEventListener('keydown', this.handleKeydown);
    
      // Stop observing the body class
      this.bodyClassObserver.disconnect();
    
      // Clean up event listeners for overlay and dismiss button
      if (this.overlay) this.overlay.removeEventListener('click', this.hideModal);
      if (this.dismissButton) this.dismissButton.removeEventListener('click', this.hideModal);
    
      // Delay clearing modal content until after the CSS transition finishes
      if (this.modalContent) {
        const timeoutId = setTimeout(() => {
          this.modalContent.innerHTML = '';
        }, 1000); // Fallback after 1s

        if (modalIsOpen) {
          this.modalContent.addEventListener('transitionend', () => {
            clearTimeout(timeoutId);
            
            // Clear the modal content
            this.modalContent.innerHTML = '';
            
            if (this.isQuickView) {
              document.querySelector('#ThemeModal')?.classList.remove('is-quick-view');
            }
          }, { once: true });
        }
      }
    
      // Return focus to the previously active element
      if (this.restoreFocusOnClose === 'true' &&
          this.previousActiveElement &&
          typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
    }

    trapFocus() {
      const focusableElements = this.modalContent.querySelectorAll(this.focusableSelector);
      const firstElement = focusableElements[0];
      
      if (!firstElement) return;

      document.addEventListener('keydown', this.handleKeydown);
      requestAnimationFrame(() => firstElement.focus());
    }

    handleKeydown(event) {
      const focusableElements = this.modalContent.querySelectorAll(this.focusableSelector);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (!firstElement || !lastElement) return;

      const isTabPressed = event.key === 'Tab';
      const isEscapePressed = event.key === 'Escape';

      if (isEscapePressed) {
        this.hideModal();
        return;
      }

      if (!isTabPressed) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }

    async fetchModalContent() {
      try {
        await this.ensureModalHtmlLoaded();

        const targetElement = document.querySelector(`#${this.modalId} ${this.modalContentSelector}`);
        if (!targetElement) {
          throw new Error('Modal content target element not found');
        }

        targetElement.innerHTML = this._prefetchedModalHTML || '';
        
        // Emit event after content is loaded
        this.isQuickView ?
          window.eventBus.emit('product:modal:ready', { detail: { sectionId: this.quickViewSectionId } })
          : window.eventBus.emit('product:modal:ready', { detail: { sectionId: this.sectionId } });
      } catch (error) {
        console.error('Theme Modal: Error fetching content', error);
        // Optionally show user-friendly error message in modal
        const targetElement = document.querySelector(`#${this.modalId} ${this.modalContentSelector}`);
        if (targetElement) {
          targetElement.innerHTML = '<p>Sorry, there was an error loading the content. Please try again.</p>';
        }
      }
    }
  }

  customElements.define('theme-modal', ThemeModal);

  if (typeof window !== 'undefined' && window.wetheme?.webcomponentRegistry) {
    window.wetheme.webcomponentRegistry.register({ key: 'theme-modal' });
  }
}

/******/ })()
;